import type { AssetsTranslations } from './types'

/**
 * Arabic translations for Assets/Fleet domain
 */
export const arTranslations: AssetsTranslations = {
  // Page/Section level
  title: "الأصول",
  subtitle: "إدارة أصول الأسطول",

  // Stats
  totalAssets: "إجمالي الأصول",
  total: "الإجمالي",
  active: "نشط",
  maintenance: "قيد الصيانة",
  idle: "معطل",
  inMaintenance: "قيد الصيانة",

  // Ownership types
  owned: "مملوك",
  leased: "مؤجر",
  subcontracted: "موكول",
  rented: "مستأجر",

  // Search and filters
  searchPlaceholder: "البحث عن الأصول...",
  noAssets: "لم يتم العثور على أصول",
  noItemsFound: "لم يتم العثور على أصول",
  noItemsDescription: "لم تقم بإضافة أي أصول بعد. أضف أول أصل للبدء.",
  addFirst: "أضف أصلك الأول",

  // Asset details
  assetDetails: "تفاصيل الأصل",
  assetId: "معرف الأصل",
  plateNumber: "رقم اللوحة",
  capacity: "السعة",
  viewHistory: "عرض السجل",
  trackLocation: "تتبع الموقع",

  // Subcontracted details
  subcontractedDetails: "تفاصيل الموكول",
  provider: "المزود",
  contractEnd: "نهاية العقد",
  monthlyCost: "التكلفة الشهرية",
  contactPerson: "جهة الاتصال",
  contactPhone: "هاتف جهة الاتصال",

  // Location info
  locationInfo: "معلومات الموقع",
  currentLocation: "الموقع الحالي",
  lastUsed: "آخر استخدام",

  // Vehicle types
  vehicleType: "نوع المركبة",
  mixer: "خلاط",
  flatbed: "سطحة",

  // Resource management
  resourceManagement: "إدارة الموارد",
  searchResources: "البحث عن الموارد...",
  createNewResource: "إنشاء مورد جديد",
  createNewResourceHint: "اختر أو أنشئ سائق ومركبة",
  noMatchingResources: "لا توجد موارد مطابقة",
  noResourcesFound: "لم يتم العثور على موارد",
  createResource: "إنشاء مورد",
  resourceName: "اسم المورد",
  resourceNamePlaceholder: "أدخل اسم المورد",
  assignedResource: "المورد المعيّن",
  driverValidation: "اسم السائق ورقم الهاتف والهوية مطلوبة",
  plateValidation: "يجب أن تتكون لوحة المركبة من 4 أرقام و3 أحرف",
  driverPhoneRequiredPlaceholder: "رقم هاتف السائق (مطلوب) مثال: 512345678",
  whatsappOptionalPlaceholder: "رقم واتساب (اختياري) مثال: 512345678",
  trailer: "المقطورة",

  // Driver section
  driver: "السائق",
  searchOrCreateDriver: "ابحث أو أنشئ سائق...",
  createNewDriver: "إنشاء سائق جديد",
  driverName: "اسم السائق",
  phoneNumber: "رقم الهاتف",
  driverId: "رقم الهوية",

  // Vehicle section
  vehicle: "المركبة",
  searchOrCreateVehicle: "ابحث أو أنشئ مركبة...",
  createNewVehicle: "إنشاء مركبة جديدة",

  // Actions
  addAsset: "إضافة أصل",
  editAsset: "تعديل الأصل",
  deleteAsset: "حذف الأصل",

  // Form section
  form: {
    name: "اسم الأصل",
    namePlaceholder: "أدخل اسم الأصل",
    status: "الحالة",
    validation: {
      nameRequired: "اسم الأصل مطلوب",
    }
  },

  // Status
  status: {
    active: "نشط",
    inactive: "غير نشط",
  },

  // Toast messages
  messages: {
    createSuccess: "تم إنشاء الأصل بنجاح",
    updateSuccess: "تم تحديث الأصل بنجاح",
    deleteSuccess: "تم حذف الأصل بنجاح",
    deleteConfirm: "هل أنت متأكد من حذف هذا الأصل؟",
  },

  // Common actions (for compatibility with shared components)
  common: {
    save: "حفظ",
    cancel: "إلغاء",
    create: "إنشاء",
    edit: "تعديل",
    delete: "حذف",
    close: "إغلاق",
    back: "رجوع",
    next: "التالي",
    submit: "إرسال",
  },

  // Table headers
  table: {
    name: "الاسم",
    status: "الحالة",
  },

  // Fleet Management Section
  fleet: {
    // Common
    title: "إدارة الأسطول",
    subtitle: "إدارة السائقين والمركبات وتعيينات الموارد",
    error: "خطأ",
    assetNumber: "رقم الأصل",
    save: "حفظ",
    saving: "جاري الحفظ...",
    cancel: "إلغاء",
    assignments: "التعيينات",
    actions: "الإجراءات",
    details: "التفاصيل",
    optional: "اختياري",
    delete: "حذف",
    confirmDelete: "هل أنت متأكد من حذف هذا العنصر؟",

    // Tabs
    resources: "الموارد",
    drivers: "السائقون",
    vehicles: "المركبات",
    trailers: "المقطورات",
    resourcesSubtitle: "تعيينات السائقين والمركبات",
    driversSubtitle: "إدارة السائقين",
    vehiclesSubtitle: "إدارة أسطول المركبات",
    trailersSubtitle: "إدارة المقطورات",
    search: "بحث...",

    // Resources
    addResource: "تعيين مورد",
    editResource: "تعديل التعيين",
    resourceName: "اسم المورد",
    noResources: "لم يتم العثور على تعيينات موارد",
    noResourcesDescription: "ابدأ بإنشاء أول تعيين مورد.",
    assignDriver: "تعيين سائق",
    assignVehicle: "تعيين مركبة",
    assignTrailer: "تعيين مقطورة",
    assignedSince: "معيّن منذ",
    currentShipment: "الشحنة الحالية",
    resourceStatus: "الحالة",
    resourceActive: "نشط",
    resourceOnDelivery: "في التوصيل",
    resourceReturning: "عائد",
    resourceInactive: "غير نشط",
    unassign: "إلغاء التعيين",
    totalResources: "إجمالي الموارد",
    activeResources: "الموارد النشطة",
    onDelivery: "في التوصيل",
    resourceDescription: "الوصف",
    resourceInformation: "معلومات المورد",
    assignment: "التعيين",
    resourceNamePlaceholderEx: "وحدة ألفا",
    resourceDescriptionPlaceholder: "وحدة التسليم الرئيسية",
    selectDriver: "اختر السائق",
    selectVehicle: "اختر المركبة",
    activateResource: "تفعيل",
    deactivateResource: "إلغاء التفعيل",
    resourceActivated: "تم تفعيل المورد",
    resourceDeactivated: "تم إلغاء تفعيل المورد",
    resourceActivatedDescription: "المورد الآن نشط ومتاح.",
    resourceDeactivatedDescription: "تم إلغاء تفعيل المورد.",
    resourceDetails: "تفاصيل المورد",
    viewResource: "عرض المورد",
    resourceCreated: "تم إنشاء المورد",
    resourceCreatedDescription: "تم إنشاء المورد بنجاح.",
    resourceUpdated: "تم تحديث المورد",
    resourceUpdatedDescription: "تم تحديث المورد بنجاح.",
    resourceUpdateError: "لا يمكن تحديث المورد: documentId مفقود",
    resourceDeleted: "تم حذف المورد",
    resourceDeletedDescription: "تم حذف المورد حذفًا مرنًا بنجاح.",
    resourceDeleteError: "لا يمكن حذف المورد: documentId مفقود",
    resourceDeleteConfirmTitle: "حذف المورد؟",
    resourceDeleteConfirmDescription: "سيؤدي هذا إلى حذف المورد حذفًا مرنًا وإلغاء تعيينه من الشحنات المعلقة أو قيد الانتظار.",

    // Drivers
    addDriver: "إضافة سائق",
    editDriver: "تعديل السائق",
    deleteDriver: "حذف السائق",
    noDrivers: "لم يتم العثور على سائقين",
    noDriversDescription: "ابدأ بإضافة أول سائق للأسطول.",
    driverName: "اسم السائق",
    driverId: "رقم الهوية",
    personalInformation: "المعلومات الشخصية",
    driverNamePlaceholder: "اسم السائق",
    driverPhoneRequiredPlaceholder: "رقم جوال السائق (مطلوب) مثال: 512345678",
    whatsappOptionalPlaceholder: "رقم واتساب (اختياري) مثال: 512345678",
    employeeId: "رقم الموظف",
    licenseNumber: "رقم الرخصة",
    licenseType: "نوع الرخصة",
    licenseExpiry: "انتهاء الرخصة",
    selectDate: "اختر التاريخ",
    licenseExpiryRequired: "تاريخ انتهاء الرخصة مطلوب.",
    licenseInformationHeading: "معلومات الرخصة",
    licenseHeavy: "مركبات ثقيلة",
    licenseLight: "مركبات خفيفة",
    licenseHazmat: "مواد خطرة",
    licenseSpecial: "خاصة",
    license: "الرخصة",
    licenseInformation: "معلومات الرخصة",
    phone: "الهاتف",
    phoneNumber: "رقم الهاتف",
    whatsappNumber: "رقم الواتساب",
    nationality: "الجنسية",
    employeeNumber: "رقم الموظف",
    driverStatus: "الحالة",
    driverAvailable: "متاح",
    driverAssigned: "معيّن",
    driverOnLeave: "في إجازة",
    driverInactive: "غير نشط",
    totalDrivers: "إجمالي السائقين",
    availableDrivers: "متاح",
    assignedDrivers: "معيّن",
    photo: "الصورة",
    driverUpdateError: "لا يمكن تحديث السائق: documentId مفقود",
    preferredLanguage: "اللغة المفضّلة",
    preferredLanguagePlaceholder: "اختر اللغة",
    langEn: "الإنجليزية",
    langAr: "العربية",
    langUr: "الأردية",

    // Vehicles
    addVehicle: "إضافة مركبة",
    editVehicle: "تعديل المركبة",
    deleteVehicle: "حذف المركبة",
    noVehicles: "لم يتم العثور على مركبات",
    noVehiclesDescription: "ابدأ بإضافة أول مركبة للأسطول.",
    vehicleType: "نوع المركبة",
    type: "النوع",
    typeTruck: "شاحنة",
    typeMixer: "خلاط",
    typeTanker: "صهريج",
    typeFlatbed: "سطحة",
    typeCrane: "رافعة",
    typeLoader: "لودر",
    typePump: "مضخة",
    typeEquipment: "معدات",
    make: "الشركة المصنعة",
    model: "الموديل",
    makeModel: "الشركة / الموديل",
    year: "السنة",
    vehicleIdentification: "تعريف المركبة",
    vehicleDetailsHeading: "تفاصيل المركبة",
    statusAndMaintenance: "الحالة والصيانة",
    makePlaceholder: "مرسيدس",
    modelPlaceholder: "أكتروس",
    yearPlaceholder: "YYYY",
    vin: "رقم الهيكل",
    plateNumber: "رقم اللوحة",
    licensePlate: "لوحة الترخيص",
    vehicleLicensePlate: "لوحة ترخيص المركبة",
    plateDigitsPlaceholder: "0000",
    plateLettersPlaceholder: "AAA",
    capacity: "السعة",
    capacityUnit: "الوحدة",
    tons: "طن",
    cubicMeters: "م³",
    vehicleStatus: "الحالة",
    vehicleAvailable: "متاح",
    vehicleAssigned: "معيّن",
    vehicleMaintenance: "صيانة",
    vehicleOutOfService: "خارج الخدمة",
    ownership: "الملكية",
    owned: "مملوك",
    leased: "مؤجر",
    rentedOut: "مؤجر للغير",
    outsourced: "خارجي",
    brokered: "وسيط",
    shared: "مشترك",
    lastMaintenance: "آخر صيانة",
    nextMaintenance: "الصيانة القادمة",
    configureColumns: "الأعمدة",
    selectVisibleColumns: "اختر الأعمدة المرئية",
    totalVehicles: "إجمالي المركبات",
    availableVehicles: "متاح",
    vehicleCreated: "تم إنشاء المركبة",
    vehicleCreatedDescription: "تم إنشاء المركبة بنجاح.",
    vehicleUpdated: "تم تحديث المركبة",
    vehicleUpdatedDescription: "تم تحديث المركبة بنجاح.",
    vehicleDeleted: "تم حذف المركبة",
    vehicleDeletedDescription: "تم حذف المركبة بنجاح.",
    vehicleUpdateError: "لا يمكن تحديث المركبة: documentId مفقود",
    makeMercedes: "مرسيدس",
    makeVolvo: "فولفو",
    makeScania: "سكانيا",
    makeMAN: "مان",
    makeDAF: "داف",
    makeIveco: "إيفيكو",
    makeHino: "هينو",
    makeIsuzu: "إيسوزو",
    makeMitsubishi: "ميتسوبيشي",
    makeRenault: "رينو",
    makeOther: "أخرى",

    // Trailers
    trailer: "مقطورة",
    addTrailer: "إضافة مقطورة",
    editTrailer: "تعديل المقطورة",
    deleteTrailer: "حذف المقطورة",
    noTrailers: "لم يتم العثور على مقطورات",
    noTrailersDescription: "ابدأ بإضافة أول مقطورة للأسطول.",
    trailerStatus: "حالة المقطورة",
    trailerAvailable: "متاح",
    trailerAssigned: "مُعيّن",
    trailerMaintenance: "صيانة",
    trailerOutOfService: "خارج الخدمة",
    selectStatus: "اختر الحالة",

    // Sort options - Drivers
    sortByName: "الاسم",
    sortNameAsc: "الاسم (أ-ي)",
    sortNameDesc: "الاسم (ي-أ)",
    sortByLicenseExpiry: "انتهاء الرخصة",
    sortLicenseExpiryAsc: "انتهاء الرخصة (الأقرب أولاً)",
    sortLicenseExpiryDesc: "انتهاء الرخصة (الأبعد أولاً)",
    sortByStatus: "الحالة",
    sortStatusAsc: "الحالة (أ-ي)",
    sortStatusDesc: "الحالة (ي-أ)",
    sortByDateAdded: "تاريخ الإضافة",
    sortOldest: "الأقدم أولاً",
    sortNewest: "الأحدث أولاً",

    // Sort options - Vehicles
    sortByPlateNumber: "رقم اللوحة",
    sortPlateAsc: "رقم اللوحة (أ-ي)",
    sortPlateDesc: "رقم اللوحة (ي-أ)",
    sortByMake: "الشركة المصنعة",
    sortMakeAsc: "الشركة المصنعة (أ-ي)",
    sortMakeDesc: "الشركة المصنعة (ي-أ)",
    sortByCapacity: "السعة",
    sortCapacityAsc: "السعة (من الأقل إلى الأعلى)",
    sortCapacityDesc: "السعة (من الأعلى إلى الأقل)",
    sortByOwnership: "الملكية",
    sortOwnershipAsc: "الملكية (أ-ي)",
    sortOwnershipDesc: "الملكية (ي-أ)",

    // Sort options - Resources
    sortByAssignedSince: "تاريخ التعيين",
    sortAssignedOldest: "التعيين الأقدم أولاً",
    sortAssignedNewest: "التعيين الأحدث أولاً",

    // Filter Labels
    filterDriverName: "اسم السائق",
    filterDriverPhone: "رقم الهاتف",
    filterEmployeeNumber: "رقم الموظف",
    filterLicensePlate: "لوحة الترخيص",
    filterBindingStatus: "حالة الربط",
    filterBindingStatusAll: "الكل",
    filterBindingStatusYes: "مُعيّن",
    filterBindingStatusNo: "غير مُعيّن",
    filterDriverNamePlaceholder: "البحث باسم السائق...",
    filterPhoneNumberPlaceholder: "البحث برقم الهاتف...",
    filterEmployeeNumberPlaceholder: "البحث برقم الموظف...",
    filterLicensePlatePlaceholder: "البحث بلوحة الترخيص...",
    filterVehicleLicensePlate: "لوحة ترخيص المركبة",
    searchByName: "البحث بالاسم",
    searchByPhone: "البحث برقم الهاتف",
    searchByEmployeeNumber: "البحث برقم الموظف",
    searchByLicensePlate: "البحث بلوحة الترخيص",
    searchByDriverName: "البحث باسم السائق",
    searchByVehiclePlate: "البحث بلوحة المركبة",
    searchByDriverPhone: "البحث بهاتف السائق",
    enterDriverName: "أدخل اسم السائق",
    enterPhoneNumber: "أدخل رقم الهاتف",
    enterEmployeeNumber: "أدخل رقم الموظف",
    enterLicensePlate: "أدخل لوحة الترخيص",
    bindingStatus: "حالة الربط",
    bindedToResource: "مربوط بمورد",
    notBinded: "غير مربوط",
    driverPhone: "هاتف السائق",

    // Detail view sections
    basicInformation: "المعلومات الأساسية",
    specifications: "المواصفات",
    additionalInfo: "معلومات إضافية",
    description: "الوصف",

    deactivationWarning: {
      title: "تم إلغاء تفعيل العنصر",
      shipmentCount: "تم إلغاء تعيين {{count}} طلب (طلبات) تلقائياً وتحتاج إلى إعادة تعيين",
      shipmentList: "الطلبات المتأثرة: {{list}}",
      resourceCount: "تم تعطيل {{count}} مورد (موارد) أيضاً",
      resourceList: "الموارد المتأثرة: {{list}}",
      viewOrders: "عرض الطلبات المتأثرة",
      dismiss: "إغلاق",
    },

    // Toast messages for fleet UI actions
    exportingTitle: 'جارٍ التصدير...',
    exportingDesc: 'جلب جميع السجلات',
    exportFailedTitle: 'فشل التصدير',
    exportDriversFailed: 'تعذّر تصدير السائقين',
    exportVehiclesFailed: 'تعذّر تصدير المركبات',
    exportResourcesFailed: 'تعذّر تصدير الموارد',
    createDriverFailed: 'فشل إنشاء السائق',
    createVehicleFailed: 'فشل إنشاء المركبة',
    createResourceFailed: 'فشل إنشاء المورد',
    importingTitle: 'جارٍ الاستيراد...',
    importingDesc: 'معالجة سجلات الملف',
    importSuccess: 'اكتمل الاستيراد',
    importSuccessDesc: '{{created}} تم الإنشاء، {{skipped}} تم التخطي',
    importFailed: 'فشل الاستيراد',
    importDriversFailed: 'تعذّر استيراد السائقين',
    importVehiclesFailed: 'تعذّر استيراد المركبات',
    importResourcesFailed: 'تعذّر استيراد المهام',
    importNoRows: 'لا توجد صفوف صالحة في الملف',
    importSelectTenantFirst: 'اختر مستأجراً أولاً',
    importSelectTenantFirstDescription: 'يرجى اختيار مستأجر محدد من شريط الأدوات العلوي قبل استيراد البيانات.',
    importDriversLabel: 'رفع السائقين',
    importVehiclesLabel: 'رفع المركبات',
    importResourcesLabel: 'رفع المهام',
  },

  // Countries
  countries: {
    saudiArabia: "المملكة العربية السعودية",
    uae: "الإمارات العربية المتحدة",
    egypt: "مصر",
    pakistan: "باكستان",
    india: "الهند",
    bangladesh: "بنغلاديش",
    philippines: "الفلبين",
    nepal: "نيبال",
    jordan: "الأردن",
    syria: "سوريا",
    yemen: "اليمن",
    sudan: "السودان",
    other: "أخرى",
  },

  // Top-level convenience keys (flat versions for direct access)
  name: "الاسم",
  namePlaceholder: "أدخل اسم الأصل",
  statusLabel: "الحالة",
  nameRequired: "اسم الأصل مطلوب",
  inactive: "غير نشط",
  createSuccess: "تم إنشاء الأصل بنجاح",
  updateSuccess: "تم تحديث الأصل بنجاح",
  deleteSuccess: "تم حذف الأصل بنجاح",
  deleteConfirm: "هل أنت متأكد من حذف هذا الأصل؟",
  actions: "الإجراءات",
  details: "التفاصيل",
  optional: "اختياري",
  delete: "حذف",
  confirmDelete: "هل أنت متأكد من حذف هذا العنصر؟",
  resources: "الموارد",
  drivers: "السائقون",
  vehicles: "المركبات",
  trailers: "المقطورات",
  resourcesSubtitle: "تعيينات السائقين والمركبات",
  driversSubtitle: "إدارة السائقين",
  vehiclesSubtitle: "إدارة أسطول المركبات",
  trailersSubtitle: "إدارة المقطورات",
  search: "بحث...",
  addResource: "تعيين مورد",
  editResource: "تعديل التعيين",
  noResources: "لم يتم العثور على تعيينات موارد",
  noResourcesDescription: "ابدأ بإنشاء أول تعيين مورد.",
  assignDriver: "تعيين سائق",
  assignVehicle: "تعيين مركبة",
  assignTrailer: "تعيين مقطورة",
  assignedSince: "معيّن منذ",
  currentShipment: "الشحنة الحالية",
  resourceStatus: "الحالة",
  resourceActive: "نشط",
  resourceOnDelivery: "في التوصيل",
  resourceReturning: "عائد",
  resourceInactive: "غير نشط",
  unassign: "إلغاء التعيين",
  totalResources: "إجمالي الموارد",
  activeResources: "الموارد النشطة",
  onDelivery: "في التوصيل",
  resourceDescription: "الوصف",
  activateResource: "تفعيل",
  deactivateResource: "إلغاء التفعيل",
  resourceActivated: "تم تفعيل المورد",
  resourceDeactivated: "تم إلغاء تفعيل المورد",
  resourceActivatedDescription: "المورد الآن نشط ومتاح.",
  resourceDeactivatedDescription: "تم إلغاء تفعيل المورد.",
  resourceDetails: "تفاصيل المورد",
  viewResource: "عرض المورد",
  addDriver: "إضافة سائق",
  editDriver: "تعديل السائق",
  deleteDriver: "حذف السائق",
  noDrivers: "لم يتم العثور على سائقين",
  noDriversDescription: "ابدأ بإضافة أول سائق للأسطول.",
  driverNameAr: "اسم السائق (بالعربية)",
  employeeId: "رقم الموظف",
  licenseNumber: "رقم الرخصة",
  licenseType: "نوع الرخصة",
  licenseExpiry: "انتهاء الرخصة",
  selectDate: "اختر التاريخ",
  licenseExpiryRequired: "تاريخ انتهاء الرخصة مطلوب.",
  licenseHeavy: "مركبات ثقيلة",
  licenseLight: "مركبات خفيفة",
  licenseHazmat: "مواد خطرة",
  licenseSpecial: "خاصة",
  phone: "الهاتف",
  whatsappNumber: "رقم الواتساب",
  driverStatus: "الحالة",
  driverAvailable: "متاح",
  driverAssigned: "معيّن",
  driverOnLeave: "في إجازة",
  driverInactive: "غير نشط",
  totalDrivers: "إجمالي السائقين",
  availableDrivers: "متاح",
  assignedDrivers: "معيّن",
  photo: "الصورة",
  addVehicle: "إضافة مركبة",
  editVehicle: "تعديل المركبة",
  deleteVehicle: "حذف المركبة",
  noVehicles: "لم يتم العثور على مركبات",
  noVehiclesDescription: "ابدأ بإضافة أول مركبة للأسطول.",
  typeTruck: "شاحنة",
  typeMixer: "خلاط",
  typeTanker: "صهريج",
  typeFlatbed: "سطحة",
  typeCrane: "رافعة",
  typeLoader: "لودر",
  typePump: "مضخة",
  typeEquipment: "معدات",
  make: "الشركة المصنعة",
  model: "الموديل",
  makeModel: "الشركة / الموديل",
  year: "السنة",
  vin: "رقم الهيكل",
  capacityUnit: "الوحدة",
  tons: "طن",
  cubicMeters: "م³",
  vehicleStatus: "الحالة",
  vehicleAvailable: "متاح",
  vehicleAssigned: "معيّن",
  vehicleMaintenance: "صيانة",
  vehicleOutOfService: "خارج الخدمة",
  ownership: "الملكية",
  rentedOut: "مؤجر للغير",
  outsourced: "خارجي",
  brokered: "وسيط",
  shared: "مشترك",
  lastMaintenance: "آخر صيانة",
  nextMaintenance: "الصيانة القادمة",
  configureColumns: "الأعمدة",
  selectVisibleColumns: "اختر الأعمدة المرئية",
  totalVehicles: "إجمالي المركبات",
  availableVehicles: "متاح",
  addTrailer: "إضافة مقطورة",
  editTrailer: "تعديل المقطورة",
  deleteTrailer: "حذف المقطورة",
  noTrailers: "لم يتم العثور على مقطورات",
  noTrailersDescription: "ابدأ بإضافة أول مقطورة للأسطول.",
  sortByName: "الاسم",
  sortNameAsc: "الاسم (أ-ي)",
  sortNameDesc: "الاسم (ي-أ)",
  sortByLicenseExpiry: "انتهاء الرخصة",
  sortLicenseExpiryAsc: "انتهاء الرخصة (الأقرب أولاً)",
  sortLicenseExpiryDesc: "انتهاء الرخصة (الأبعد أولاً)",
  sortByStatus: "الحالة",
  sortStatusAsc: "الحالة (أ-ي)",
  sortStatusDesc: "الحالة (ي-أ)",
  sortByDateAdded: "تاريخ الإضافة",
  sortOldest: "الأقدم أولاً",
  sortNewest: "الأحدث أولاً",
  sortByPlateNumber: "رقم اللوحة",
  sortPlateAsc: "رقم اللوحة (أ-ي)",
  sortPlateDesc: "رقم اللوحة (ي-أ)",
  sortByMake: "الشركة المصنعة",
  sortMakeAsc: "الشركة المصنعة (أ-ي)",
  sortMakeDesc: "الشركة المصنعة (ي-أ)",
  sortByCapacity: "السعة",
  sortCapacityAsc: "السعة (من الأقل إلى الأعلى)",
  sortCapacityDesc: "السعة (من الأعلى إلى الأقل)",
  sortByOwnership: "الملكية",
  sortOwnershipAsc: "الملكية (أ-ي)",
  sortOwnershipDesc: "الملكية (ي-أ)",
  sortByAssignedSince: "تاريخ التعيين",
  sortAssignedOldest: "التعيين الأقدم أولاً",
  sortAssignedNewest: "التعيين الأحدث أولاً",
  filterDriverName: "اسم السائق",
  filterDriverPhone: "رقم الهاتف",
  filterEmployeeNumber: "رقم الموظف",
  filterLicensePlate: "لوحة الترخيص",
  filterBindingStatus: "حالة الربط",
  filterBindingStatusAll: "الكل",
  filterBindingStatusYes: "مُعيّن",
  filterBindingStatusNo: "غير مُعيّن",
  filterDriverNamePlaceholder: "البحث باسم السائق...",
  filterPhoneNumberPlaceholder: "البحث برقم الهاتف...",
  filterEmployeeNumberPlaceholder: "البحث برقم الموظف...",
  filterLicensePlatePlaceholder: "البحث بلوحة الترخيص...",
  filterVehicleLicensePlate: "لوحة ترخيص المركبة",
  saudiArabia: "المملكة العربية السعودية",
  uae: "الإمارات العربية المتحدة",
  egypt: "مصر",
  pakistan: "باكستان",
  india: "الهند",
  bangladesh: "بنغلاديش",
  philippines: "الفلبين",
  nepal: "نيبال",
  jordan: "الأردن",
  syria: "سوريا",
  yemen: "اليمن",
  sudan: "السودان",
  other: "أخرى",

  // Section headings
  vehicleIdentification: "تعريف المركبة",
  vehicleDetailsHeading: "تفاصيل المركبة",
  statusAndMaintenance: "الحالة والصيانة",
  personalInformation: "المعلومات الشخصية",
  licenseInformationHeading: "معلومات الرخصة",
  trailerInformation: "معلومات المقطورة",
  resourceInformation: "معلومات المورد",
  assignment: "التعيين",

  // Placeholders
  makePlaceholder: "مرسيدس",
  modelPlaceholder: "أكتروس",
  yearPlaceholder: "YYYY",
  driverNamePlaceholder: "الاسم",
  licensePlaceholder: "DL-12345",
  trailerMakePlaceholder: "أدخل الماركة",
  trailerModelPlaceholder: "أدخل الموديل",
  vinPlaceholder: "أدخل رقم VIN",
  descriptionPlaceholder: "أدخل الوصف",
  resourceNamePlaceholderEx: "وحدة ألفا",
  resourceDescriptionPlaceholder: "وحدة التسليم الرئيسية",
  plateDigitsPlaceholder: "0000",
  plateLettersPlaceholder: "AAA",
  trailerIdPlaceholder: "TRL-001",
  trailerPlatePlaceholder: "ABC-123",
  trailerYearPlaceholder: "2024",
  licensePlateExamplePlaceholder: "مثال: 5080-ZZR",

  // Select options
  selectDriver: "اختر السائق",
  selectVehicle: "اختر المركبة",

  errors: {
    driverNotFound: 'السائق غير موجود',
    vehicleNotFound: 'المركبة غير موجودة',
    trailerNotFound: 'المقطورة غير موجودة',
    resourceNotFound: 'المورد غير موجود',
  },
}
